import { createContext, useContext, useState, useEffect } from 'react';
import { ParsedUrlQuery } from 'querystring';

/*
 	This context provides a utility function, routeHandler(), to help you handle routing in the application
	and avoid patterns where you would have to pass down a prop through multiple levels
	of components in order to apply routing to a link.

	Per default, the Link atom component utilizes RouteContext, making it possible for you to route a
	link without having to manually pass a click handler to it.
	To override this behavior, simply pass the 'disableRouteHandler' prop to the Link component.

	The routeHandler utility function can also be utilized in other use cases where you might want to
	handle routing but don't want to use the Link component.

	To use the routeHandler, import useRouteContext and call it like this:
	const routeHandler = useRouteContext();
	<SomeComponent onClick={(e) => routeHandler(e, '/some/path')} />
*/

type RouteHandler = (event: React.MouseEvent | React.ChangeEvent<HTMLSelectElement>, url: string, locale?: string, target?: string) => void;

type ShallowRoutingOptions = { shallow?: boolean };

interface RouterEventCallbacks {
	routeChangeStart: (url: string, options: ShallowRoutingOptions) => void;
	routeChangeComplete: (url: string, options: ShallowRoutingOptions) => void;
	routeChangeError: (err: Error & { cancelled?: boolean }, url: string, options: ShallowRoutingOptions) => void;
	beforeHistoryChange: (url: string, options: ShallowRoutingOptions) => void;
	hashChangeStart: (url: string, options: ShallowRoutingOptions) => void;
	hashChangeComplete: (url: string, options: ShallowRoutingOptions) => void;
}

type RouterEvent = keyof RouterEventCallbacks;

interface RouterEvents {
	on<E extends RouterEvent>(event: E, callback: RouterEventCallbacks[E]): void;
	off<E extends RouterEvent>(event: E, callback: RouterEventCallbacks[E]): void;
	emit<E extends RouterEvent>(event: E, ...args: Parameters<RouterEventCallbacks[E]>): void;
}

type Router = {
	pathname: string;
	route: string;
	query: ParsedUrlQuery;
	asPath: string;
	isFallback: boolean;
	basePath: string;
	locale?: string;
	locales?: string[];
	defaultLocale?: string;
	isReady: boolean;
	isPreview: boolean;
	isLocaleDomain: boolean;
	events: RouterEvents;
};

interface RouteContextProviderProps {
	children?: React.ReactNode;
	router: {
		push: (url: string, as?: string, options?: { locale: string }) => void;
		events?: RouterEvents;
	};
}

export const RouteContext = createContext(null);

export const RouteContextProvider = (props: RouteContextProviderProps) => {
	const { children, router } = props;
	const [routeLoading, setRouteLoading] = useState(false);

	useEffect(() => {
		if (!router?.events) return;

		const handleStart = () => {
			setRouteLoading(true);
		};

		const handleComplete = () => {
			setRouteLoading(false);
		};

		router.events.on('routeChangeStart', handleStart);
		router.events.on('routeChangeComplete', handleComplete);
		router.events.on('routeChangeError', handleComplete);

		return () => {
			router.events.off('routeChangeStart', handleStart);
			router.events.off('routeChangeComplete', handleComplete);
			router.events.off('routeChangeError', handleComplete);
		};
	}, [router]);

	const routeHandler: RouteHandler = (event, url, locale, target) => {
		if (!router) return;

		const { ctrlKey, shiftKey } = event as React.MouseEvent;
		const hasTargetNewWindow = ctrlKey || shiftKey || target === '_blank';

		// Use default browser behavior if the link has target="_blank" or if the user is holding down the ctrl or shift key.
		if (hasTargetNewWindow) return;

		event?.preventDefault();

		const isInternalLink = url.startsWith('/') || url.startsWith('#') || url.startsWith('?') || url.startsWith(window.origin);

		// Reroute if internal link. Otherwise just redirect.
		// As stated in the Next.js docs (https://nextjs.org/docs/api-reference/next/router):
		// "You don't need to use router.push for external URLs. window.location is better suited for those cases."

		if (!isInternalLink) {
			window.location.href = url;
			return;
		}

		if (locale) {
			router.push(url, url, { locale: locale });
			return;
		}

		router.push(url);
	};

	return <RouteContext.Provider value={{ routeHandler, router, routeLoading }}>{children}</RouteContext.Provider>;
};

/**
 * @function useRouteContext
 * @returns {RouteHandler, Router, routeLoading} routeHandler(event, href)
 */
export const useRouteContext = (): { routeHandler: RouteHandler; router: Router; routeLoading: boolean } => {
	return useContext(RouteContext);
};
